import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { Avatar } from '@/components/ui/avatar'
import { useAuth } from '@/hooks/useAuth'
import { cn } from '@/lib/utils'
import { useMyProfile } from '@/hooks/useMyProfile'

/** Figma `panel-list` with a label on the left and its value on the right. */
function ValueRow({
  label,
  value,
  last = false,
}: {
  label: string
  value: string
  last?: boolean
}): ReactElement {
  return (
    <div
      data-name="panel-list"
      className={cn('flex w-full items-center gap-4 pb-4', !last && 'hub-rule')}
    >
      <div className="flex min-w-px flex-1 items-start justify-between gap-4">
        <span className="hub-title-lg text-list-text shrink-0">{label}</span>
        <span className="hub-title-lg text-list-text-secondary truncate text-right">{value}</span>
      </div>
    </div>
  )
}

function initialsOf(name: string, email: string): string {
  const source = name.trim() || email
  const parts = source.split(/[\s.@]+/).filter(Boolean)
  return (
    parts
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase() || '?'
  )
}

/**
 * Account tab of the user panel — Figma `panel` content
 * (2256:284299 and the 1200/900/600/400 frames).
 *
 * Email come from the Entra ID token (useAuth); everything else comes
 * from the profile endpoint (useMyProfile) once it resolves.
 */
export function AccountTab(): ReactElement {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { data: profile } = useMyProfile()
  const name = user?.name ?? ''
  const email = user?.email ?? ''

  return (
    <>
      <div data-name="panel-list" className="hub-rule flex w-full items-center gap-4 pb-4">
        <span className="hub-title-lg text-list-text min-w-px flex-1">{t('account.avatar')}</span>
        <Avatar initials={initialsOf(name, email)} className="size-[36px] lg:size-[36px]" />
      </div>

      <ValueRow label={t('account.name')} value={name} />
      <ValueRow label={t('account.email')} value={email} />
      <ValueRow label={t('account.educationId')} value={profile?.educationalIdentifier || '—'} />
      <ValueRow label={t('account.studentId')} value={profile?.studentCardNumber || '—'} />
      <ValueRow label={t('account.neptunCode')} value={profile?.neptunCode || '—'} />
      <ValueRow last label={t('account.cusmanId')} value={profile?.cusmanId || '—'} />
    </>
  )
}
